import React from "react";
import useReveal from "./useReveal";

const PROJECTS = [
  {
    icon: "⚡",
    title: "WebVitals Doctor",
    desc: "A full-stack web performance analysis tool that runs real Lighthouse audits via headless Chromium in the cloud. Converts Core Web Vitals into actionable insights with historical regression tracking.",
    tags: [
      "React",
      "TypeScript",
      "Node.js",
      "Puppeteer",
      "Supabase",
      "Lighthouse",
    ],
    live: "https://webvitals-doctor.netlify.app/",
    code: "https://github.com/ramakrishnakatragadda/webvitals-ui",
  },
  {
    icon: "🏗",
    title: "This Portfolio",
    desc: "Designed and built from scratch. Dark terminal aesthetic, custom cursor, multilingual typing effect, scroll animations, and a writing section. React, no UI library — every detail deliberate.",
    tags: ["React", "CSS-in-JS", "Design", "Animation"],
    live: "https://ramakrishnakatragadda.in",
    code: "https://github.com/ramakrishnakatragadda/personal-portfolio",
  },
  {
    icon: "🔭",
    title: "Next project",
    desc: "Something's cooking. Always is.",
    tags: ["Soon™"],
    live: null,
    code: null,
    placeholder: true,
  },
];

const ProjectCard = ({ project, delay }) => {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      style={{
        ...s.card,
        ...(project.placeholder ? s.placeholder : {}),
        opacity: visible ? (project.placeholder ? 0.55 : 1) : 0,
        transform: visible ? "translateY(0)" : "translateY(20px)",
        transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
      }}
    >
      <div style={s.cardHeader}>
        <div style={s.icon}>{project.icon}</div>
        <div style={s.links}>
          {project.live && (
            <a
              href={project.live}
              target="_blank"
              rel="noreferrer"
              style={s.link}
            >
              Live ↗
            </a>
          )}
          {project.code && (
            <a
              href={project.code}
              target="_blank"
              rel="noreferrer"
              style={s.link}
            >
              Code ↗
            </a>
          )}
        </div>
      </div>
      <div style={s.title}>{project.title}</div>
      <div style={s.desc}>{project.desc}</div>
      <div style={s.tags}>
        {project.tags.map((t) => (
          <span key={t} style={s.tag}>
            {t}
          </span>
        ))}
      </div>
    </div>
  );
};

const ProjectsSection = () => {
  const [labelRef, labelVisible] = useReveal();
  return (
    <section id="projects" className="section-pad" style={s.section}>
      <div
        ref={labelRef}
        style={{ opacity: labelVisible ? 1 : 0, transition: "opacity 0.5s" }}
      >
        <p style={s.label}>
          <span style={s.slash}>//</span> Projects
        </p>
      </div>
      <div className="projects-grid">
        {PROJECTS.map((p, i) => (
          <ProjectCard key={p.title} project={p} delay={i * 0.1} />
        ))}
      </div>
    </section>
  );
};

const s = {
  section: { maxWidth: 1100, margin: "0 auto" },
  label: {
    fontSize: "0.68rem",
    letterSpacing: "0.2em",
    textTransform: "uppercase",
    color: "var(--accent)",
    marginBottom: "2.5rem",
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
  },
  slash: { color: "var(--muted)" },
  grid: {},
  card: {
    background: "var(--bg2)",
    border: "1px solid var(--border)",
    borderRadius: 6,
    padding: "2rem",
    position: "relative",
    overflow: "hidden",
  },
  placeholder: { borderStyle: "dashed" },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "1rem",
  },
  icon: {
    width: 38,
    height: 38,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--bg3)",
    border: "1px solid var(--border)",
    borderRadius: 6,
    fontSize: "1.1rem",
  },
  links: { display: "flex", gap: "0.6rem" },
  link: {
    fontSize: "0.67rem",
    color: "var(--muted)",
    textDecoration: "none",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    border: "1px solid var(--border)",
    padding: "4px 10px",
    borderRadius: 3,
  },
  title: {
    fontSize: "1rem",
    fontWeight: 500,
    color: "var(--text)",
    marginBottom: "0.5rem",
  },
  desc: {
    fontSize: "0.83rem",
    color: "var(--muted2)",
    lineHeight: 1.85,
    marginBottom: "1.25rem",
  },
  tags: { display: "flex", flexWrap: "wrap", gap: "0.4rem" },
  tag: {
    fontSize: "0.66rem",
    background: "var(--bg3)",
    border: "1px solid var(--border)",
    color: "var(--muted)",
    padding: "3px 8px",
    borderRadius: 3,
    letterSpacing: "0.06em",
  },
};

export default ProjectsSection;
